import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';

import { GetProducts } from '@/apis/onboarding';
import { getProductDDL } from '@/common/utils/dropDown';

export interface ProductLookupItem {
  id: string;
  name: string;
}

interface PaginatedProductsPayload {
  is_success?: boolean;
  message?: string;
  data?: Array<{ id: string; name: string }>;
  page_count?: number;
  total_count?: number;
  requirement_count?: number;
}

interface UsePaginatedProductDropdownParams {
  token: string;
  enabled?: boolean;
  productsRefreshKey?: number;
  unauthorize?: (error: unknown) => void;
}

const mergeIntoLookupRecord = (
  prev: Record<string, ProductLookupItem>,
  items: Array<{ id: string; name: string }>
): Record<string, ProductLookupItem> => {
  const next = { ...prev };
  for (const item of items) {
    if (item.id) {
      next[item.id] = { id: item.id, name: item.name };
    }
  }
  return next;
};

export const usePaginatedProductDropdown = ({
  token,
  enabled = true,
  productsRefreshKey,
  unauthorize,
}: UsePaginatedProductDropdownParams) => {
  const [products, setProducts] = useState<ProductLookupItem[]>([]);
  const [productLookup, setProductLookup] = useState<
    Record<string, ProductLookupItem>
  >({});
  const [search, setSearch] = useState('');
  const [searchInputKey, setSearchInputKey] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [catalogTotalCount, setCatalogTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadingRef = useRef(false);
  const lastFetchedSearchRef = useRef<string | null>(null);
  const lastProductsRefreshKeyRef = useRef<number | undefined>(
    productsRefreshKey
  );
  const skipNextSearchFetchRef = useRef(false);
  const suppressScrollLoadUntilRef = useRef(0);
  const scrollElRef = useRef<HTMLDivElement>(null);

  const mergeIntoLookup = useCallback(
    (items: Array<{ id: string; name: string }>) => {
      setProductLookup((prev) => mergeIntoLookupRecord(prev, items));
    },
    []
  );

  const applyProductsResponse = useCallback(
    (
      payload: PaginatedProductsPayload,
      append: boolean,
      nextPage: number,
      searchValue: string
    ): boolean => {
      if (!payload?.is_success) {
        message.error(payload?.message || 'Failed to fetch products');
        return false;
      }

      const incoming = (payload.data || []).map((item) => ({
        id: item.id,
        name: item.name,
      }));

      setProducts((prev) => (append ? [...prev, ...incoming] : incoming));
      setProductLookup((prev) => mergeIntoLookupRecord(prev, incoming));
      setTotalPages(payload.page_count || 1);
      const resolvedTotalCount = payload.total_count ?? incoming.length;
      setTotalCount(resolvedTotalCount);
      if (!searchValue) {
        setCatalogTotalCount(resolvedTotalCount);
      }
      setPage(nextPage);
      return true;
    },
    []
  );

  const loadFullCatalogFallback = useCallback(
    async (searchValue: string) => {
      try {
        const fullList = await getProductDDL(token);
        if (!fullList?.length) {
          setProducts([]);
          setTotalCount(0);
          if (!searchValue) {
            setCatalogTotalCount(0);
          }
          lastFetchedSearchRef.current = searchValue;
          return;
        }

        const mapped = fullList.map((item) => ({
          id: item.id,
          name: item.name,
        }));
        setProducts(mapped);
        setProductLookup((prev) => mergeIntoLookupRecord(prev, mapped));
        setTotalPages(1);
        setTotalCount(mapped.length);
        if (!searchValue) {
          setCatalogTotalCount(mapped.length);
        }
        setPage(1);
        lastFetchedSearchRef.current = searchValue;
      } catch (error) {
        unauthorize?.(error);
        message.error('Failed to fetch products');
        setProducts([]);
        lastFetchedSearchRef.current = searchValue;
      }
    },
    [token, unauthorize]
  );

  const fetchProducts = useCallback(
    async (searchValue: string, pageNum = 1, append = false) => {
      if (!token) return;

      loadingRef.current = true;
      setLoading(true);

      try {
        const response = await GetProducts(
          token,
          pageNum,
          searchValue || undefined
        );
        const payload = response?.data?.data as
          PaginatedProductsPayload | undefined;

        if (
          payload &&
          applyProductsResponse(payload, append, pageNum, searchValue)
        ) {
          lastFetchedSearchRef.current = searchValue;
          return;
        }

        if (!payload) {
          await loadFullCatalogFallback(searchValue);
        } else {
          lastFetchedSearchRef.current = searchValue;
        }
      } catch (error) {
        unauthorize?.(error);
        await loadFullCatalogFallback(searchValue);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [token, applyProductsResponse, loadFullCatalogFallback, unauthorize]
  );

  const fetchMoreProducts = useCallback(() => {
    if (loadingRef.current || page >= totalPages) return;
    fetchProducts(search, page + 1, true);
  }, [fetchProducts, page, search, totalPages]);

  const handlePopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      if (!target) return;

      scrollElRef.current = target;

      if (Date.now() < suppressScrollLoadUntilRef.current) {
        target.scrollTop = 0;
        return;
      }

      const nearBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - 24;

      if (nearBottom) {
        fetchMoreProducts();
      }
    },
    [fetchMoreProducts]
  );

  const refreshFromFirstPage = useCallback(async () => {
    setProducts([]);
    setPage(1);
    setTotalPages(1);
    setSearchInputKey((prev) => prev + 1);
    suppressScrollLoadUntilRef.current = Date.now() + 750;
    scrollElRef.current = null;
    lastFetchedSearchRef.current = null;

    if (search !== '') {
      skipNextSearchFetchRef.current = true;
      setSearch('');
    }

    await fetchProducts('');
  }, [fetchProducts, search]);

  useEffect(() => {
    if (!enabled || !token) return;

    if (skipNextSearchFetchRef.current) {
      skipNextSearchFetchRef.current = false;
      return;
    }

    if (search === lastFetchedSearchRef.current) {
      return;
    }

    const debounceMs = lastFetchedSearchRef.current === null ? 0 : 400;
    const timer = setTimeout(() => {
      fetchProducts(search, 1, false);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [enabled, token, search, fetchProducts]);

  useEffect(() => {
    if (productsRefreshKey === undefined) return;

    if (lastProductsRefreshKeyRef.current === productsRefreshKey) {
      lastProductsRefreshKeyRef.current = productsRefreshKey;
      return;
    }

    lastProductsRefreshKeyRef.current = productsRefreshKey;
    refreshFromFirstPage();
  }, [productsRefreshKey, refreshFromFirstPage]);

  const getProductName = useCallback(
    (id: string) => productLookup[id]?.name ?? '',
    [productLookup]
  );

  return {
    products,
    productLookup,
    getProductName,
    mergeIntoLookup,
    search,
    setSearch,
    searchInputKey,
    loading,
    totalCount,
    catalogTotalCount,
    handlePopupScroll,
    scrollElRef,
  };
};
